/**
 * Links from the Compliance Overview to the exact list behind each number it
 * shows, and the URL contract the detail pages read to open pre-filtered.
 *
 * The overview used to link to each page's default view, so "11 expired
 * credentials" opened the whole staff list and "10 denied lines" opened the
 * pending queue. Every link now carries what it counted plus from=overview,
 * which the page uses to filter and to show OverviewDrillDownNotice.
 */

export const OVERVIEW_PATH = '/admin/compliance/overview';
export const FROM_OVERVIEW = 'overview';

export const COMPLIANCE_PAGES = Object.freeze({
  credentials: '/admin/compliance/staff-credentials',
  authorizations: '/admin/compliance/authorization-guardrails',
  documents: '/admin/compliance/document-expiration',
  supervision: '/admin/compliance/pca-supervision',
  billing: '/admin/compliance/billing-compliance',
});

/** A detail-page link that opens filtered to `params` and remembers it came from the overview. */
export function overviewLink(page, params = {}) {
  const path = COMPLIANCE_PAGES[page] || page;
  const query = new URLSearchParams({ ...params, from: FROM_OVERVIEW });
  return `${path}?${query.toString()}`;
}

/**
 * Every drill-down the overview offers. Each detail page maps these params
 * onto its own filters:
 *   staff-credentials        tab=credentials|compliance, status=expired|expiring|missing
 *   authorization-guardrails view=expiring|utilization, status=expired|expiring|renewal_pending
 *   document-expiration      tab=clinical|ads, status=expired|expiring|overdue|due, days=7|14
 *   pca-supervision          status=overdue|due_soon|new_worker
 *   billing-compliance       status=denied, days=30
 */
export const DRILL_DOWNS = Object.freeze({
  credentialsExpired: overviewLink('credentials', { tab: 'credentials', status: 'expired' }),
  credentialsExpiring: overviewLink('credentials', { tab: 'credentials', status: 'expiring' }),
  staffMissingRequired: overviewLink('credentials', { tab: 'compliance', status: 'missing' }),
  authorizationsExpired: overviewLink('authorizations', { view: 'expiring', status: 'expired' }),
  authorizationsExpiring: overviewLink('authorizations', { view: 'expiring', status: 'expiring' }),
  authorizationsRenewalPending: overviewLink('authorizations', { view: 'expiring', status: 'renewal_pending' }),
  authorizationsHighUse: overviewLink('authorizations', { view: 'utilization' }),
  clinicalExpired: overviewLink('documents', { tab: 'clinical', status: 'expired' }),
  clinicalExpiring7: overviewLink('documents', { tab: 'clinical', status: 'expiring', days: '7' }),
  clinicalExpiring14: overviewLink('documents', { tab: 'clinical', status: 'expiring', days: '14' }),
  adcOverdue: overviewLink('documents', { tab: 'ads', status: 'overdue' }),
  adcDue7: overviewLink('documents', { tab: 'ads', status: 'due', days: '7' }),
  adcDue14: overviewLink('documents', { tab: 'ads', status: 'due', days: '14' }),
  supervisionOverdue: overviewLink('supervision', { status: 'overdue' }),
  supervisionDueSoon: overviewLink('supervision', { status: 'due_soon' }),
  // Both open the New Worker tile, which lists due and overdue together.
  pcaWorkerObservationOverdue: overviewLink('supervision', { status: 'new_worker' }),
  pcaWorkerObservationDue: overviewLink('supervision', { status: 'new_worker' }),
  billingDenied: overviewLink('billing', { status: 'denied', days: '30' }),
});

/**
 * Read a detail page's drill-down params.
 *
 * @param {string} search  window.location.search or useLocation().search
 * @returns {{ fromOverview: boolean, get: (name: string) => string | null }}
 */
export function readDrillDown(search = '') {
  let params;
  try { params = new URLSearchParams(search); } catch { params = new URLSearchParams(); }
  return {
    fromOverview: params.get('from') === FROM_OVERVIEW,
    get: (name) => params.get(name),
  };
}

/** YYYY-MM-DD for `days` before today (local calendar), for "last N days" filters. */
export function daysAgoDateInput(days, today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - Number(days || 0));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
